import { useState, useMemo, useCallback } from 'react';
import { useUsersList, useDeleteUser } from '@/features/users/hooks/useUsers';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useDeploymentsList } from '@/features/deployments/hooks/useDeployments';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, userRoleMeta, radius } from '@/shared/theme/tokens';
import { PageHeader } from '@/shared/ui/PageHeader';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useApiErrorToast } from '@/shared/ui/Toast';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { AppTable } from '@/shared/ui/AppTable';
import { useModalStyles } from '@/shared/ui/modalStyles';
import { ROLES } from '@/entities/rbac/lib/permissions';
import { SearchInput } from '@/shared/ui/SearchInput';
import { UserFormModal } from './UserFormModal';

const RIPIK_ROLES = new Set([ROLES.RIPIK_ADMIN, ROLES.RIPIK_USER]);


export default function UsersPage() {
  const { canManageUsers } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: users, isLoading, error } = useUsersList();
  const { data: allDeployments = [] } = useDeploymentsList();

  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const toastApiError = useApiErrorToast();
  const deleteUser = useDeleteUser();

  const ripikUsers = useMemo(() => {
    if (!users) return [];
    return users.filter((u) =>
      u.assignments?.some((a) => RIPIK_ROLES.has(a.role))
    );
  }, [users]);

  const filtered = useMemo(() => {
    if (!search.trim()) return ripikUsers;
    const q = search.toLowerCase();
    return ripikUsers.filter((u) =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [ripikUsers, search]);

  async function handleDelete(user) {
    try {
      await deleteUser.mutateAsync(user.id);
      setConfirmDelete(null);
    } catch (err) { toastApiError(err); }
  }

  return (
    <div style={{ fontFamily: typography.fontFamily.body, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Users"
        description={`${ripikUsers.length ?? 0} ripik users`}
        actions={
          canManageUsers && (
            <PrimaryButton icon="person_add" onClick={() => setModal({ mode: 'create' })}>
              Add User
            </PrimaryButton>
          )
        }
      />

      {/* Search bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name or email…" />
      </div>

      {/* Table */}
      {error ? (
        <ErrorState message={error.message} />
      ) : !isLoading && filtered.length === 0 ? (
        <EmptyState hasFilters={!!search} onClear={() => setSearch('')} />
      ) : (
        <UsersTable
          users={filtered}
          isLoading={isLoading}
          canManage={canManageUsers}
          currentUserId={currentUserId}
          allDeployments={allDeployments}
          onEdit={(u) => setModal({ mode: 'edit', user: u })}
          onDelete={(u) => setConfirmDelete(u)}
        />
      )}

      {modal && (
        <UserFormModal
          mode={modal.mode}
          user={modal.user}
          onClose={() => setModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          user={confirmDelete}
          loading={deleteUser.isPending}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

function UsersTable({ users, isLoading, canManage, currentUserId, allDeployments, onEdit, onDelete }) {
  const c = useThemeColors();
  const getRowId = useCallback((r) => r.id, []);

  const columns = useMemo(() => {
    const cols = [
      {
        field: 'name',
        headerName: 'User',
        flex: 1.4,
        minWidth: 200,
        renderCell: ({ row }) => {
          const initials = row.name?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() ?? '?';
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: radius.md,
                background: 'linear-gradient(135deg, #1e4396 0%, #3EB5E7 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: '#ffffff', flexShrink: 0, letterSpacing: typography.letterSpacing.wide,
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>{row.name}</div>
                <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{row.email}</div>
              </div>
            </div>
          );
        },
      },
      {
        field: '__role',
        headerName: 'Top Role',
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) => {
          const roles = row.assignments?.map((a) => a.role) ?? [];
          const topRole = [ROLES.RIPIK_ADMIN, ROLES.RIPIK_USER, ROLES.CLIENT_ADMIN, ROLES.CLIENT_USER].find((r) => roles.includes(r));
          return topRole ? <RoleBadge role={topRole} /> : <span style={{ fontSize: typography.fontSize.base, color: c.borderInput }}>—</span>;
        },
      },
      {
        field: '__deployments',
        headerName: 'Deployments',
        flex: 1.4,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {row.assignments?.slice(0, 3).map((a) => {
              const dep = allDeployments.find((d) => d.id === a.deploymentId);
              return dep ? (
                <span key={a.deploymentId} style={{
                  display: 'inline-flex', padding: '3px 8px', borderRadius: radius.sm,
                  background: c.bgMuted, border: `1px solid ${c.borderSubtle}`,
                  fontSize: typography.fontSize.sm, color: c.textLabel, fontWeight: typography.fontWeight.medium, whiteSpace: 'nowrap',
                }}>
                  {dep.name.split('–').pop()?.trim() ?? dep.name}
                </span>
              ) : null;
            })}
            {(row.assignments?.length ?? 0) > 3 && (
              <span style={{
                display: 'inline-flex', padding: '3px 8px', borderRadius: radius.sm,
                background: c.bgMuted, border: `1px solid ${c.borderSubtle}`,
                fontSize: typography.fontSize.sm, color: c.textSubtle, fontWeight: typography.fontWeight.medium, whiteSpace: 'nowrap',
              }}>
                +{row.assignments.length - 3} more
              </span>
            )}
            {row.assignments?.length === 0 && <span style={{ fontSize: typography.fontSize.base, color: c.borderInput }}>No access</span>}
          </div>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        field: '__actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const isSelf = row.id === currentUserId;
          if (isSelf) return <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, fontStyle: 'italic' }}>You</span>;
          return (
            <div style={{ display: 'flex', gap: '4px' }}>
              <button style={{
                width: '28px', height: '28px', borderRadius: radius.md, background: c.bg,
                border: `1px solid ${c.borderSubtle}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', padding: 0,
              }} onClick={() => onEdit(row)} title="Edit user">
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: c.textMuted }}>edit</span>
              </button>
              <button style={{
                width: '28px', height: '28px', borderRadius: radius.md, background: c.bg,
                border: `1px solid rgba(186,26,26,0.2)`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', padding: 0,
              }} onClick={() => onDelete(row)} title="Delete user">
                <span className="material-symbols-outlined" style={{ fontSize: 15, color: semantic.error }}>delete</span>
              </button>
            </div>
          );
        },
      });
    }

    return cols;
  }, [canManage, currentUserId, allDeployments, onEdit, onDelete, c]);

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <AppTable rows={users} columns={columns} getRowId={getRowId} getRowHeight={() => 'auto'} loading={isLoading} />
    </div>
  );
}

function RoleBadge({ role }) {
  const meta = userRoleMeta[role] ?? userRoleMeta[ROLES.CLIENT_USER];
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 9px',
      borderRadius: radius.full,
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
      color: meta.text,
      letterSpacing: typography.letterSpacing.wide,
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
}

// ─── Confirm Delete ───────────────────────────────────────────────────────────

function ConfirmDeleteModal({ user, loading, onConfirm, onCancel }) {
  const c = useThemeColors();
  const { overlayStyle, modalStyle } = useModalStyles('400px');
  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ width: 40, height: 40, borderRadius: radius.lg, background: semantic.errorContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: semantic.error }}>person_remove</span>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>Remove User</h3>
            <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textMuted }}>This cannot be undone.</p>
          </div>
        </div>
        <p style={{ fontSize: typography.fontSize.lg, color: c.textLabel, margin: '0 0 20px', lineHeight: 1.5 }}>
          Remove <strong>{user.name}</strong> ({user.email}) from the platform?
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <PrimaryButton variant="secondary" onClick={onCancel} disabled={loading}>Cancel</PrimaryButton>
          <PrimaryButton variant="danger" onClick={onConfirm} disabled={loading} icon="person_remove">
            {loading ? 'Removing…' : 'Remove User'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Empty / Skeleton / Error ─────────────────────────────────────────────────

function EmptyState({ hasFilters, onClear }) {
  const c = useThemeColors();
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.borderInput, display: 'block', marginBottom: '12px' }}>
        {hasFilters ? 'search_off' : 'group'}
      </span>
      <h3 style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textLabel, margin: '0 0 6px' }}>
        {hasFilters ? 'No users match' : 'No users yet'}
      </h3>
      <p style={{ fontSize: typography.fontSize.md, color: c.textSubtle, margin: '0 0 16px' }}>
        {hasFilters ? 'Try clearing the filters.' : 'Add your first user to get started.'}
      </p>
      {hasFilters && (
        <PrimaryButton variant="secondary" onClick={onClear} icon="close" size="sm">Clear filters</PrimaryButton>
      )}
    </div>
  );
}


